// Public (no-login) printer download page — opened from the token link the
// admin copies on the orders page. WeTransfer-style: one card per order, the
// albums + their cover/interior PDFs inside, plus a "Télécharger tout (ZIP)".
// Works on mobile; the link expires after 7 days (worker returns 410 → we show
// the expired notice). Data comes from GET /orders/printer-link/public/:token,
// which re-derives the tree + serves fresh URLs on every visit.
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ordersApi } from '@/lib/api';
import { zip } from 'fflate';
import { generateProductionSheetPdf } from '@/lib/productionSheet';
import { couponNoteForAlbum, type CouponPrintPlace } from '@/lib/couponLeaves';

type LinkData = Awaited<ReturnType<typeof ordersApi.printerLinkPublic>>;
type OrderRow = LinkData['orders'][number];
type State = 'loading' | 'ready' | 'expired' | 'notfound' | 'error';

/** COUPONS (plan 2026-10-28): where this order's printed coupons are —
 *  orders.coupon_print, plus a signed link when they are a separate PDF. Both
 *  absent (switch off, or a worker that does not send them) → nothing shows. */
type CouponFields = { coupon_print?: CouponPrintPlace; coupon_pdf_url?: string | null };
const couponsOf = (o: OrderRow): CouponFields => o as OrderRow & CouponFields;

const tokenize = (s: string) => (s || 'commande').replace(/^#/, '').replace(/[^a-zA-Z0-9._-]/g, '_');

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
}

export default function PrinterLinkPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<LinkData | null>(null);
  const [state, setState] = useState<State>('loading');
  const [zipping, setZipping] = useState<string | null>(null); // order_id being zipped
  const [allState, setAllState] = useState<string | null>(null); // "Tout" progress label
  const [failed, setFailed] = useState<string | null>(null);

  // Fetch one order's PDFs as a {name: bytes} map. `folder` nests them under the
  // order number (for the combined "tout" zip so names don't collide); flat for a
  // single-order zip.
  async function fetchOrderFiles(o: OrderRow, folder: boolean): Promise<Record<string, Uint8Array>> {
    const tok = tokenize(o.order_name);
    const prefix = folder ? `${tok}/` : '';
    const files: Record<string, Uint8Array> = {};

    // Production sheet FIRST. The paid-option instruction used to live only on
    // this web page, so it vanished the moment the printer downloaded the ZIP
    // and worked from a folder. Putting it inside the archive means the
    // instruction travels with the job. Best-effort: a failure here must never
    // cost the printer their PDFs.
    try {
      files[`${prefix}${tok}_FICHE-PRODUCTION.pdf`] =
        await generateProductionSheetPdf({ ...o, upsells: sheetExtras(o) }, new Date());
    } catch (e) {
      console.error('[printer-zip] production sheet failed:', e);
    }

    for (const a of o.albums) {
      const tag = `${prefix}${tok}_${a.album_index}of${o.albums.length}`;
      if (a.cover_url) files[`${tag}_couverture.pdf`] = new Uint8Array(await (await fetch(a.cover_url)).arrayBuffer());
      if (a.interior_url) files[`${tag}_interieur.pdf`] = new Uint8Array(await (await fetch(a.interior_url)).arrayBuffer());
    }
    // The separate coupon sheet (no album had a free leaf) travels with the job.
    const couponUrl = couponsOf(o).coupon_pdf_url;
    if (couponUrl) files[`${prefix}${tok}_COUPONS-a-decouper.pdf`] = new Uint8Array(await (await fetch(couponUrl)).arrayBuffer());
    return files;
  }

  // PDFs barely compress, so store (level 0) = fast. Triggers a browser download.
  function zipAndDownload(files: Record<string, Uint8Array>, filename: string): Promise<void> {
    return new Promise((resolve, reject) => {
      zip(files, { level: 0 }, (e, bytes) => {
        if (e) { reject(e); return; }
        const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/zip' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        resolve();
      });
    });
  }

  // One order → one ZIP, built on demand in the printer's browser.
  async function downloadOrderZip(o: OrderRow) {
    setZipping(o.order_id);
    setFailed(null);
    try {
      await zipAndDownload(await fetchOrderFiles(o, false), `commande_${tokenize(o.order_name)}.zip`);
    } catch (e) {
      console.error('[printer-zip] failed:', e);
      setFailed('Le ZIP n’a pas pu être créé — réessayez, ou téléchargez les fichiers un par un.');
    } finally {
      setZipping(null);
    }
  }

  // "Tout télécharger": ONE combined ZIP (folders per order) for a moderate
  // batch; for a large one, download each order's ZIP in turn so the browser
  // never holds a multi-hundred-MB archive in memory.
  async function downloadAll() {
    if (!data) return;
    const orders = data.orders.filter((o) => o.zip_available);
    if (orders.length === 0) return;
    const totalAlbums = orders.reduce((s, o) => s + o.albums.length, 0);
    setAllState('Préparation…');
    try {
      if (totalAlbums <= 12) {
        let files: Record<string, Uint8Array> = {};
        for (let i = 0; i < orders.length; i++) {
          setAllState(`Préparation ${i + 1}/${orders.length}…`);
          files = { ...files, ...(await fetchOrderFiles(orders[i], true)) };
        }
        await zipAndDownload(files, `commandes_impression_${new Date().toISOString().slice(0, 10)}.zip`);
      } else {
        for (let i = 0; i < orders.length; i++) {
          setAllState(`Téléchargement ${i + 1}/${orders.length}…`);
          await downloadOrderZip(orders[i]);
        }
      }
    } catch (e) {
      console.error('[printer-zip-all] failed:', e);
      setFailed('Le téléchargement groupé a échoué — téléchargez les commandes une par une.');
    } finally {
      setAllState(null);
    }
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) { setState('notfound'); return; }
      try {
        const d = await ordersApi.printerLinkPublic(token);
        if (!alive) return;
        setData(d);
        setState('ready');
      } catch (e: unknown) {
        if (!alive) return;
        const msg = e instanceof Error ? e.message : '';
        setState(msg === 'expired' ? 'expired' : msg === 'not_found' ? 'notfound' : 'error');
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const totalAlbums = data?.orders.reduce((s, o) => s + o.albums.length, 0) ?? 0;
  const reliefOrders = data?.orders.filter(hasRelief).length ?? 0;
  const urgentOrders = data?.orders.filter((o) => o.urgent).length ?? 0;
  // URGENT first (express delivery bought): the ones he starts with.
  const orders = [...(data?.orders ?? [])].sort((a, b) => Number(!!b.urgent) - Number(!!a.urgent));
  const redoCount = data?.redos?.length ?? 0;

  return (
    <div className="min-h-screen bg-[#F7F4EC] text-[#1F1A12]">
      <header className="border-b border-[#E6DDC6] bg-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold tracking-[0.12em] text-brand-700">STORIZ</span>
            <span className="text-sm text-[#7A6F5C]">Atelier d'impression</span>
          </div>
          {state === 'ready' && data && (
            <span className="text-xs text-[#7A6F5C] text-right">Lien valable jusqu'au {fmtDate(data.expires_at)}</span>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 sm:py-10">
        {state === 'loading' && <p className="text-sm text-[#7A6F5C]">Chargement…</p>}
        {state === 'expired' && <Notice title="Lien expiré" body="Ce lien de téléchargement a expiré (valable 7 jours). Demandez-en un nouveau à Storiz." />}
        {state === 'notfound' && <Notice title="Lien introuvable" body="Ce lien n'existe pas ou a été révoqué." />}
        {state === 'error' && <Notice title="Erreur" body="Impossible de charger ce lien pour le moment. Réessayez." />}

        {state === 'ready' && data && (
          <div className="space-y-6">
            {/* What to print, in one line, and the one button that gets it all. */}
            <section className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-semibold leading-tight">
                  {totalAlbums > 0
                    ? <>{totalAlbums} album{totalAlbums > 1 ? 's' : ''} à imprimer</>
                    : <>Réimpression</>}
                </h1>
                <p className="text-sm text-[#7A6F5C] mt-1">
                  {[
                    data.orders.length > 0 ? `${data.orders.length} commande${data.orders.length > 1 ? 's' : ''}` : null,
                    redoCount > 0 ? `${redoCount} réimpression${redoCount > 1 ? 's' : ''}` : null,
                  ].filter(Boolean).join(' · ')}
                </p>
              </div>
              {data.orders.some((o) => o.zip_available) && (
                <button
                  onClick={downloadAll}
                  disabled={allState !== null}
                  className="w-full sm:w-auto min-h-12 px-6 rounded-xl bg-[#1F1A12] text-white text-sm font-semibold hover:bg-black transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 cursor-pointer"
                >
                  <DownloadIcon /> {allState ?? 'Tout télécharger (ZIP)'}
                </button>
              )}
            </section>

            {failed && (
              <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{failed}</p>
            )}

            {urgentOrders > 0 && (
              <div className="rounded-xl border-2 border-red-600 bg-red-50 px-4 py-3 flex gap-3 items-start">
                <span aria-hidden className="shrink-0 rounded-md bg-red-600 text-white text-[11px] font-bold tracking-wider px-2 py-0.5 mt-0.5">URGENT</span>
                <p className="text-sm text-red-950">
                  <strong>{urgentOrders} commande{urgentOrders > 1 ? 's' : ''} urgente{urgentOrders > 1 ? 's' : ''}.</strong>{' '}
                  À imprimer en priorité — {urgentOrders > 1 ? 'elles sont' : 'elle est'} en haut de la liste.
                </p>
              </div>
            )}

            {/* Relief first: the one instruction that changes how a job is made. */}
            {reliefOrders > 0 && (
              <div className="rounded-xl border-2 border-brand-500 bg-brand-50 px-4 py-3 flex gap-3 items-start">
                <span aria-hidden className="text-brand-700 text-xl leading-none mt-0.5">✦</span>
                <p className="text-sm text-[#3D3020]">
                  <strong>{reliefOrders} commande{reliefOrders > 1 ? 's' : ''} avec Relief 3D.</strong>{' '}
                  Vernis sélectif sur le titre et les icônes de la couverture — pas sur le fond ni sur les photos.
                  Les albums concernés sont marqués en doré.
                </p>
              </div>
            )}

            <div className="space-y-4">
              {orders.map((o, oi) => {
                const relief = hasRelief(o);
                return (
                  <article key={oi} className={`rounded-2xl bg-white overflow-hidden border ${o.urgent ? 'border-red-600 border-2' : relief ? 'border-brand-500 border-2' : 'border-[#E6DDC6]'}`}>
                    <div className="px-4 sm:px-5 py-3 flex items-center justify-between gap-3 border-b border-[#EFE8D6]">
                      <div className="min-w-0">
                        <h2 className="text-base font-semibold font-sans">Commande {o.order_name}</h2>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          <span className="text-xs text-[#7A6F5C] mr-1">{o.albums.length} album{o.albums.length > 1 ? 's' : ''}</span>
                          {o.urgent && (
                            <span className="rounded-md bg-red-600 text-white text-[11px] font-bold tracking-wider px-2 py-0.5">URGENT</span>
                          )}
                          {relief && (
                            <span className="rounded-md bg-brand-700 text-white text-[11px] font-bold tracking-wider px-2 py-0.5">✦ RELIEF 3D SÉLECTIF</span>
                          )}
                        </div>
                      </div>
                      {o.zip_available && (
                        <button
                          onClick={() => downloadOrderZip(o)}
                          disabled={zipping === o.order_id}
                          className="shrink-0 min-h-10 px-4 rounded-lg border border-[#1F1A12] text-[#1F1A12] text-xs font-semibold hover:bg-[#1F1A12] hover:text-white transition-colors inline-flex items-center gap-1.5 disabled:opacity-60 cursor-pointer"
                        >
                          <DownloadIcon /> {zipping === o.order_id ? 'Compression…' : 'ZIP'}
                        </button>
                      )}
                    </div>

                    {relief && (
                      <div className="px-4 sm:px-5 py-2.5 bg-brand-50 border-b border-brand-200 text-sm text-[#3D3020] flex flex-wrap gap-x-2">
                        <strong className="text-brand-800">✦ Relief 3D sélectif</strong>
                        <span>— {reliefLabel(o)}.</span>
                      </div>
                    )}

                    {o.albums.length === 0 && (
                      <p className="px-5 py-4 text-sm text-[#7A6F5C]">Aucun PDF disponible pour cette commande.</p>
                    )}
                    <ul className="divide-y divide-[#F1ECDF]">
                      {o.albums.map((a) => (
                        <li key={a.album_index} className="px-4 sm:px-5 py-4 flex gap-4 items-center">
                          <CoverThumb url={a.cover_preview_url ?? null} relief={relief} />
                          <div className="min-w-0 flex-1 space-y-2">
                            <div>
                              <p className="font-semibold text-[15px] leading-snug">
                                Album {a.album_index}/{o.albums.length}{a.destination ? ` — ${a.destination}` : ''}
                              </p>
                              {a.pages ? <p className="text-xs text-[#7A6F5C] tabular-nums">{a.pages} pages intérieures</p> : null}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <FileBtn url={a.cover_url} label="Couverture" />
                              <FileBtn url={a.interior_url} label="Intérieur" />
                            </div>
                            {couponNoteForAlbum(couponsOf(o).coupon_print, a.album_index) && (
                              <p className="text-xs font-semibold text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 inline-block">
                                ✂ {couponNoteForAlbum(couponsOf(o).coupon_print, a.album_index)} — ne pas relier
                              </p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                    {couponsOf(o).coupon_pdf_url && (
                      <div className="px-4 sm:px-5 py-3 border-t border-[#F1ECDF] flex flex-wrap items-center gap-3">
                        <FileBtn url={couponsOf(o).coupon_pdf_url ?? null} label="Coupons (à découper)" />
                        <span className="text-xs text-[#7A6F5C]">1 feuille recto/verso, même papier que l’intérieur — à découper, ne pas relier.</span>
                      </div>
                    )}
                  </article>
                );
              })}

              {(data.redos ?? []).map((r) => (
                <article key={r.redo_id} className="rounded-2xl border-2 border-amber-300 bg-white overflow-hidden">
                  <div className="px-4 sm:px-5 py-3 border-b border-amber-100 bg-amber-50 flex items-center justify-between gap-3">
                    <h2 className="text-base font-semibold font-sans min-w-0">Réimpression {r.order_name} <span className="font-normal text-[#7A6F5C]">— album {r.album_index}</span></h2>
                    <span className="shrink-0 px-2.5 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[11px] font-bold tracking-wide">REDO</span>
                  </div>
                  <div className="px-4 sm:px-5 py-4 space-y-3">
                    <p className="text-sm font-medium">
                      {r.pages?.length
                        ? <>Pages à réimprimer : {r.pages.join(', ')} <span className="font-normal text-[#7A6F5C]">(le PDF intérieur ne contient QUE ces pages)</span></>
                        : r.scope === 'cover-only' ? 'Couverture uniquement'
                        : r.scope === 'interior-only' ? 'Intérieur complet'
                        : 'Album complet'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <FileBtn url={r.cover_url} label="Couverture" />
                      <FileBtn url={r.interior_url} label="Intérieur" />
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <p className="text-xs text-[#7A6F5C] pt-4 text-center">
              Fichiers PDF haute qualité, prêts à imprimer à l'échelle 1:1. Le ZIP contient aussi la fiche de production.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

/** The worker folds every source of a relief yes into `relief_3d` (2026-10-12).
 *  ⚠️ Not « any upsell »: an express-only order must not be varnished. */
function hasRelief(o: OrderRow): boolean {
  return o.relief_3d === true;
}

/** The atelier's own words for it (products.printer_label). */
function reliefLabel(o: OrderRow): string {
  return o.relief_label?.trim() || 'Vernis sélectif';
}

/** The paid work for the production sheet in the ZIP, from the same flags as
 *  the page — a yes given in WhatsApp is not an order_items line. */
function sheetExtras(o: OrderRow): Array<{ label: string; quantity: number }> {
  const out: Array<{ label: string; quantity: number }> = [];
  if (o.urgent) out.push({ label: 'URGENT - imprimer en priorite', quantity: 1 });
  if (hasRelief(o)) out.push({ label: `Relief 3D selectif - ${reliefLabel(o)}`, quantity: o.albums.length || 1 });
  return out;
}

function CoverThumb({ url, relief }: { url: string | null; relief: boolean }) {
  return (
    <div className={`relative shrink-0 w-[72px] sm:w-[84px] aspect-[0.7] rounded-md overflow-hidden bg-[#EFE8D6] ${relief ? 'ring-2 ring-brand-500 ring-offset-2' : 'border border-[#E6DDC6]'}`}>
      {url
        ? <img src={url} alt="Couverture" loading="lazy" className="w-full h-full object-cover" />
        : <span className="absolute inset-0 flex items-center justify-center text-[10px] text-[#7A6F5C] text-center px-1">Aperçu indisponible</span>}
      {relief && (
        <span className="absolute bottom-1 left-1 right-1 rounded bg-brand-700/90 text-white text-[9px] font-bold text-center py-0.5 tracking-wide">✦ RELIEF</span>
      )}
    </div>
  );
}

function DownloadIcon() {
  return (
    <svg aria-hidden width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v11" /><path d="m7 11 5 5 5-5" /><path d="M5 20h14" />
    </svg>
  );
}

function FileBtn({ url, label }: { url: string | null; label: string }) {
  if (!url) {
    return <span className="min-h-10 px-3 inline-flex items-center rounded-lg bg-[#F1ECDF] text-[#9A8F7C] text-xs">{label} — indisponible</span>;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="min-h-10 px-3.5 rounded-lg border border-[#E6DDC6] bg-[#FBF8F1] text-[#1F1A12] text-xs font-medium hover:border-brand-500 hover:bg-brand-50 inline-flex items-center gap-1.5 transition-colors"
    >
      <DownloadIcon /> {label} <span className="text-[#9A8F7C]">PDF</span>
    </a>
  );
}

function Notice({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-[#E6DDC6] bg-white p-8 text-center">
      <h1 className="text-2xl font-semibold mb-1">{title}</h1>
      <p className="text-sm text-[#7A6F5C]">{body}</p>
    </div>
  );
}
